/**
 * SSR breakdown helpers — leg-aware grouping for booking-details surfaces.
 *
 * Industry standard: a 4-segment round-trip with the same 15 KG bag attached
 * to every segment renders as TWO baggage rows (onward + return), not four.
 * Meals stay per-segment because they're consumed per-flight; seats stay
 * per-segment because each seat is its own product.
 *
 * Source of truth (in priority order):
 *   1. `booking.leg_baggage_summary` — precomputed by the backend on book.
 *      Carries explicit `leg_index`, `leg_label`, `leg_route`, `pax_name`,
 *      `code`, `description`, `total_amount`, `segment_count`.
 *   2. Derived on the fly from `booking.ssr_breakdown` — for legacy
 *      bookings persisted before the leg-summary field shipped. Falls back
 *      to the row's `leg` field ("onward" / "return"), so multi-city
 *      degrades gracefully.
 */

const _legSortKey = (legLabel) => (legLabel === 'onward' ? 0 : legLabel === 'return' ? 1 : 2);

export function buildLegBaggageRowsFromBreakdown(ssrBreakdown, legRouteLabels) {
  const rows = Array.isArray(ssrBreakdown) ? ssrBreakdown : [];
  const labels = legRouteLabels && typeof legRouteLabels === 'object' ? legRouteLabels : {};
  const grouped = new Map();

  rows.forEach((r) => {
    if (!r || typeof r !== 'object') return;
    if (String(r.kind || '').toLowerCase() !== 'baggage') return;
    const legIndex = Number.isFinite(Number(r.leg_index))
      ? Number(r.leg_index)
      : (r.leg === 'return' ? 1 : 0);
    const code = String(r.code || '').trim();
    if (!code) return;
    const paxIndex = Number(r.pax_index || 0) || 0;
    const key = `${paxIndex}|${legIndex}|${code}`;
    const amt = Number(r.amount || 0) || 0;
    if (!grouped.has(key)) {
      grouped.set(key, {
        pax_index: paxIndex,
        pax_name: r.pax_name || '',
        pax_ref: String(r.pax_ref || paxIndex),
        leg_index: legIndex,
        leg_label: legIndex === 0 ? 'onward' : 'return',
        leg_route: labels[String(legIndex)] || labels[legIndex] || '',
        code,
        description: r.description || code,
        total_amount: amt,
        segment_count: 1,
      });
    } else {
      const e = grouped.get(key);
      e.total_amount += amt;
      e.segment_count += 1;
    }
  });

  return Array.from(grouped.values())
    .map((e) => ({ ...e, total_amount: Math.round(e.total_amount * 100) / 100 }))
    .sort(
      (a, b) =>
        a.pax_index - b.pax_index
        || a.leg_index - b.leg_index
        || a.code.localeCompare(b.code),
    );
}

/**
 * Pick the best baggage-by-leg view available for a booking:
 *   - prefer backend `leg_baggage_summary` (carries supplier leg route),
 *   - otherwise derive from `ssr_breakdown`.
 */
export function getLegBaggageRowsForBooking(booking) {
  if (!booking || typeof booking !== 'object') return [];
  if (Array.isArray(booking.leg_baggage_summary) && booking.leg_baggage_summary.length) {
    return booking.leg_baggage_summary;
  }
  return buildLegBaggageRowsFromBreakdown(
    booking.ssr_breakdown,
    booking.leg_route_labels,
  );
}

/** Non-baggage SSR rows (meals + seats stay per-segment). */
export function getNonBaggageSsrRowsForBooking(booking) {
  const rows = Array.isArray(booking?.ssr_breakdown) ? booking.ssr_breakdown : [];
  return rows.filter((r) => String(r?.kind || '').toLowerCase() !== 'baggage');
}

/** Group rows by leg-label for display. Returns { onward: [...], return: [...] }. */
export function groupSsrRowsByLeg(legBaggageRows, nonBaggageRows) {
  const out = { onward: [], return: [], other: [] };
  (legBaggageRows || []).forEach((r) => {
    const bucket = (r.leg_label === 'return' ? 'return' : r.leg_label === 'onward' ? 'onward' : 'other');
    out[bucket].push({ ...r, _displayKind: 'baggage' });
  });
  (nonBaggageRows || []).forEach((r) => {
    const bucket = (r.leg === 'return' ? 'return' : r.leg === 'onward' ? 'onward' : 'other');
    out[bucket].push({ ...r, _displayKind: String(r.kind || '').toLowerCase() });
  });
  return out;
}

export function sortLegKeys(grouped) {
  return Object.keys(grouped || {}).filter((k) => (grouped[k] || []).length).sort((a, b) => _legSortKey(a) - _legSortKey(b));
}

export const _testables = {
  _legSortKey,
};
